import { z } from "zod";
import { uuidLoose } from "./uuid.js";
/**
 * Wire-format DTOs for the admin Customers list/detail/PATCH/reject
 * endpoints (`/v1/admin/customers/*`, P08-T3).
 *
 * Field naming follows the existing admin-controller convention: snake_case
 * keys mirroring database columns (see `packages/db/src/schema/identity.ts`).
 *
 * The customer's email lives on `users.email` (FK customers.user_id ->
 * users.id), not on `customers` directly. The list/detail queries JOIN to
 * surface it on the wire row.
 */
// ---------- Enums (mirrored from packages/db/src/schema/identity.ts) ----------
export const CustomerTierValues = [
    "standard",
    "bronze",
    "silver",
    "gold",
];
export const CustomerTier = z.enum(CustomerTierValues);
/**
 * V1.0 customer-tier discount percentages, MIRRORED from
 * `packages/services-pricing/src/tiers.ts`. The pricing kernel is the
 * authoritative source — this re-export lives in shared-types so the
 * admin SPA can render tier labels (e.g. "Silver — 5%") without pulling
 * the pricing kernel into the admin bundle.
 *
 * The two maps MUST stay in sync. The lockstep is enforced by
 * `tier-discount-sync.test.ts` in the services-pricing package — if you
 * change a percentage here, change it there too (and vice versa) or the
 * test fails CI. Promotion to a DB-backed `customer_tiers` table is
 * V1.5+ scope, after which both files become a fallback / startup cache.
 */
export const TIER_DISCOUNT_PCT = {
    standard: 0,
    bronze: 2.5,
    silver: 5,
    gold: 7.5,
};
/**
 * Title-case label for a tier (e.g. "Silver"). The wire format is the
 * lowercase tier value; this helper produces the human-readable label
 * for SPA rendering. Used by `formatTierWithDiscount` and direct list /
 * detail rendering.
 */
export function tierLabel(tier) {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
}
/**
 * Human-readable tier label with its V1.0 discount percentage suffixed,
 * e.g. `"Silver — 5%"`. Used in:
 *  - admin customer-list "Tier" column
 *  - admin customer-detail page header
 *  - admin tier dropdowns (CustomerEditForm, CustomerApproveForm)
 *
 * Numeric formatting drops trailing `.0` (so 5 renders as "5%" not
 * "5.0%") but keeps `.5` precision (so 2.5 renders as "2.5%").
 */
export function formatTierWithDiscount(tier) {
    const pct = TIER_DISCOUNT_PCT[tier];
    const pctStr = Number.isInteger(pct) ? pct.toString() : pct.toFixed(1);
    return `${tierLabel(tier)} — ${pctStr}%`;
}
export const CustomerRoleValues = ["admin", "purchaser", "viewer"];
export const CustomerRole = z.enum(CustomerRoleValues);
export const CustomerApprovalStatusValues = [
    "pending",
    "approved",
    "rejected",
];
export const CustomerApprovalStatus = z.enum(CustomerApprovalStatusValues);
// ---------- Request bodies ----------
export const ListCustomersQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: CustomerApprovalStatus.optional(),
    company_id: uuidLoose.optional(),
    q: z.string().max(200).optional(),
});
/**
 * PATCH body for post-approval edits. At least one of `tier`, `role`, or
 * `default_location_id` must be present — the empty-object PATCH rejects
 * with 400 so callers can't quietly noop.
 */
export const UpdateCustomerBody = z
    .object({
    tier: CustomerTier.optional(),
    role: CustomerRole.optional(),
    default_location_id: uuidLoose.optional(),
    spending_limit_minor: z.coerce.number().int().min(0).nullable().optional(),
})
    .refine((v) => v.tier !== undefined ||
    v.role !== undefined ||
    v.default_location_id !== undefined ||
    v.spending_limit_minor !== undefined, {
    message: "at least one of tier, role, default_location_id, spending_limit_minor is required",
});
export const RejectCustomerBody = z.object({
    reason: z.string().min(1).max(500),
});
/**
 * POST /v1/admin/customers/:id/locations body. The M:N grant references a
 * `company_locations.id`; snake_case mirrors the rest of admin DTOs (the
 * plan's `{companyLocationId}` literal is rewritten to repo convention per
 * L1 — see customer-locations.controller.ts for the use site).
 */
export const AssignCustomerLocationBody = z.object({
    company_location_id: uuidLoose,
});
